import {useMutation, useQueryClient, type InfiniteData} from '@tanstack/react-query';
import {
    createCustomer,
    createServiceArea,
    createServiceTemplate,
    deleteServiceArea,
    refreshCustomerTaxRates,
    regenerateSchedule,
    type ReviewAction,
    reviewVisit,
    updateCustomer,
    updateOrganization,
    updateOrganizationEventWebhook,
    updateServiceArea,
    provideSubscriptionRate,
    updateServiceTemplate,
    deactivateServiceTemplate,
    markNotificationAsRead,
    markNotificationAsUnread,
    markAllNotificationsAsRead,
    type GetNotificationsResponse,
    updateCrew,
    updateCrewMemberRole,
    removeMemberFromCrew,
} from '../lib/api';
import {
    createSingleVisit,
    updateVisit,
    completeVisitReview,
    updateSubscriptionSchedule,
} from '../lib/api/routes/visits';
import {sendWelcomeEmails, decideAccessRequest, patchCustomerSubscription} from '../lib/api/routes/customers';
import {updateNotificationTemplate, resetNotificationTemplate} from '../lib/api/routes/notificationTemplates';
import type {NotificationPreferences, UpdateUserProfileRequest} from '../lib/api/routes/users';
import {updateNotificationPreferences, updateUserProfile} from '../lib/api/routes/users';
import type {Customer, CreateServiceAreaRequest, UpdateServiceAreaRequest, ServiceTemplate} from '@ruta/shared';
import type {CreateServiceTemplateData} from '../lib/types';
import {createServiceBundle, deactivateServiceBundle, updateServiceBundle} from '../lib/api/routes/service-bundles';
import type {CreateServiceBundleData, UpdateServiceBundleData} from '../lib/api/routes/service-bundles';
import {createPaymentPlan, updatePaymentPlan, cancelPaymentPlan, recordInstallmentPayment} from '../lib/api/routes/payment-plans';
import type {CreatePaymentPlanData, UpdatePaymentPlanData, RecordInstallmentPaymentData} from '../lib/api/routes/payment-plans';
import {queryKeys} from '../lib/queryKeys';
import { toCustomerDetailCache } from '../lib/customerDetailCache';
import { clearWorkQueueItems } from '../lib/workQueueStats';

// Customer Mutations
export function useCreateCustomerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => createCustomer(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.list() });
      queryClient.invalidateQueries({ queryKey: ['customers', 'search'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.stats() });
      queryClient.setQueryData(
        queryKeys.customers.detail(result.customer.id),
        toCustomerDetailCache(result.customer),
      );
    },
  });
}

/**
 * Owner's decision on a pending access request (RUT-2228): approve the requester
 * onto this account (JOIN) or spin them off as a separate customer (SEPARATE).
 */
export function useDecideAccessRequestMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerId, contactType, contactValue, decision }: {
      customerId: string;
      contactType: 'EMAIL' | 'PHONE';
      contactValue: string;
      decision: 'JOIN' | 'SEPARATE';
    }) => decideAccessRequest(customerId, { contactType, contactValue, decision }),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.pendingAccessRequests(variables.customerId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.addressNeighbors(variables.customerId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.detail(variables.customerId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.list() });
      queryClient.invalidateQueries({ queryKey: ['customers', 'search'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.stats() });
    },
  });
}

export function useUpdateCustomerSubscriptionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ customerId, serviceTemplateId, data }: {
      customerId: string;
      serviceTemplateId: string;
      data: any
    }) => {
      return provideSubscriptionRate(customerId, serviceTemplateId, data);
    },
    onSuccess: () => {
      // Not invalidating to prevent flash
    },
  });
}

export function useUpdateCustomerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerId, data }: { customerId: string; data: Partial<Customer> }) =>
      updateCustomer(customerId, data),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.stats() });
      queryClient.setQueryData(
        queryKeys.customers.detail(variables.customerId),
        (old) => toCustomerDetailCache(result.customer, old ?? undefined),
      );
    },
  });
}

export function useRefreshCustomerTaxRatesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerId, forceRefresh }: { customerId: string; forceRefresh?: boolean }) =>
      refreshCustomerTaxRates(customerId, { forceRefresh }),
    onSuccess: (result, variables) => {
      // Update the customer detail query with new tax data
      queryClient.setQueryData(
        queryKeys.customers.detail(variables.customerId),
        (oldData: any) => {
          if (!oldData?.customer) return oldData;
          return {
            ...oldData,
            customer: {
              ...oldData.customer,
              zipCode: result.customer.zipCode,
              taxRate: result.customer.taxRate,
              taxRateData: result.customer.taxRateData,
            },
          };
        }
      );
      // Also invalidate the customers list to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all() });
    },
  });
}

// Service Template Mutations
export function useCreateServiceTemplateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateServiceTemplateData) => createServiceTemplate(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.serviceTemplates.all() });
      queryClient.setQueryData(
        queryKeys.serviceTemplates.detail(result.template.id),
        result.template
      );
    },
  });
}

// Service Bundle Mutations
export function useCreateServiceBundleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateServiceBundleData) => createServiceBundle(data),
    onSuccess: (bundle) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.serviceBundles.all() });
      queryClient.setQueryData(queryKeys.serviceBundles.detail(bundle.id), bundle);
    },
  });
}

export function useUpdateServiceBundleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateServiceBundleData }) =>
      updateServiceBundle(id, data),
    onSuccess: (bundle, variables) => {
      queryClient.setQueryData(queryKeys.serviceBundles.detail(variables.id), bundle);
      queryClient.invalidateQueries({ queryKey: queryKeys.serviceBundles.all() });
    },
  });
}

/** Retire a package: it stops being offered; customers already sold it keep it. */
export function useDeactivateServiceBundleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deactivateServiceBundle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.serviceBundles.all() });
    },
  });
}

// Payment Plan Mutations (RUT-2215)
export function useCreatePaymentPlanMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerId, data }: { customerId: string; data: CreatePaymentPlanData }) =>
      createPaymentPlan(customerId, data),
    onSuccess: (_plan, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.paymentPlans.customer(variables.customerId),
      });
    },
  });
}

export function useUpdatePaymentPlanMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customerId,
      planId,
      data,
    }: {
      customerId: string;
      planId: string;
      data: UpdatePaymentPlanData;
    }) => updatePaymentPlan(customerId, planId, data),
    onSuccess: (_plan, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.paymentPlans.customer(variables.customerId),
      });
    },
  });
}

export function useCancelPaymentPlanMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerId, planId }: { customerId: string; planId: string }) =>
      cancelPaymentPlan(customerId, planId),
    onSuccess: (_plan, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.paymentPlans.customer(variables.customerId),
      });
    },
  });
}

export function useRecordInstallmentPaymentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customerId,
      planId,
      installmentId,
      data,
    }: {
      customerId: string;
      planId: string;
      installmentId: string;
      data: RecordInstallmentPaymentData;
    }) => recordInstallmentPayment(customerId, planId, installmentId, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.paymentPlans.customer(variables.customerId),
      });
      // Paying an installment moves the customer's balance and ledger.
      queryClient.invalidateQueries({ queryKey: ['customerLedger', variables.customerId] });
      queryClient.invalidateQueries({ queryKey: ['customer', variables.customerId] });
    },
  });
}

export function useUpdateServiceTemplateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ServiceTemplate> }) => {
      return updateServiceTemplate(id, data);
    },
    onSuccess: (result, variables) => {
      // Set the updated data immediately
      queryClient.setQueryData(
        queryKeys.serviceTemplates.detail(variables.id),
        result.template
      );
      // Then invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.serviceTemplates.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.serviceTemplates.detail(variables.id) });
    },
  });
}

export function useDeactivateServiceTemplateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateId: string) => {
      return deactivateServiceTemplate(templateId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.serviceTemplates.all() });
    },
  });
}

// Organization Mutations
export function useUpdateOrganizationMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => updateOrganization(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.current() });
    },
  });
}

/**
 * Save or clear the automation doorbell (RUT-3817). Separate from the company
 * tab's debounced auto-save on purpose — a write-only secret should leave the
 * browser once, on an explicit Save, not on every keystroke after 2 seconds.
 */
export function useUpdateOrganizationEventWebhookMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { mcpEventWebhookUrl: string; mcpEventWebhookSecret?: string }) =>
      updateOrganizationEventWebhook(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.current() });
    },
  });
}

// Visit Mutations
export function useCreateVisitMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      customerId: string;
      serviceTemplateId: string;
      crewId?: string;
      budgetedHours?: number;
      adminNotes?: string;
    }) => {
      // Create the visit first
      const result = await createSingleVisit({
        customerId: data.customerId,
        serviceTemplateId: data.serviceTemplateId,
        crewId: data.crewId,
        budgetedHours: data.budgetedHours,
      });

      // If admin notes provided, update the visit
      if (data.adminNotes && result.visit.id) {
        await updateVisit(result.visit.id, {
          adminNotes: data.adminNotes,
        });
      }

      return result;
    },
    onSuccess: () => {
      // Invalidate visits to refresh the list
      queryClient.invalidateQueries({ queryKey: ['visits'] });
    },
  });
}

export function useReviewVisitMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ visitId, reviewAction }: { visitId: string; reviewAction: ReviewAction }) =>
      reviewVisit(visitId, reviewAction),
    onSuccess: () => {
      // Invalidate review queue to refresh the list
      queryClient.invalidateQueries({ queryKey: ['visits', 'review-queue'] });
    },
  });
}

export function useCompleteVisitReviewMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ visitId, review }: {
      visitId: string;
      review: {
        action: 'approved' | 'adjusted' | 'requires_follow_up';
        reviewNotes: string;
        adjustedAmount?: number;
        adjustmentReason?: string;
      }
    }) => {
      return completeVisitReview(visitId, review);
    },
    onSuccess: (_data, { review }) => {
      // Invalidate visits requiring review to refresh the list
      queryClient.invalidateQueries({ queryKey: ['visits', 'requiring-review'] });
      queryClient.invalidateQueries({ queryKey: ['visits', 'review-queue'] });
      // Resolving a review clears its "Review Required" activity-feed to-do on
      // the server (RUT-2703); refetch notifications so the feed drops the card.
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      // Follow-up stays in the queue. Approve / adjust leave it — drop the
      // header badge now; do not wait for the visit WebSocket (RUT-3668).
      if (review.action !== 'requires_follow_up') {
        clearWorkQueueItems(queryClient, 'visitsRequiringReview');
      }
    },
  });
}

// Decline (dismiss) a pending rate request straight from the dashboard alert
// (RUT-3335). Uses the same `dismiss` action the service-detail view uses — it
// clears the request server-side and dismisses the admin alert; the customer is
// NOT notified. Invalidating notifications drops the (now-dismissed) card.
export function useDismissRateRequestMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerId, serviceTemplateId }: { customerId: string; serviceTemplateId: string }) =>
      patchCustomerSubscription(customerId, serviceTemplateId, { action: 'dismiss' }),
    onSuccess: (_data, { customerId }) => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['customers', customerId] });
      queryClient.invalidateQueries({ queryKey: ['pipeline', 'pendingRates'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'stats'] });
    },
  });
}

export function useRegenerateScheduleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dateRange: { from: string; to: string }) => regenerateSchedule(dateRange),
    onSuccess: () => {
      // Invalidate visits to refresh the list
      queryClient.invalidateQueries({ queryKey: ['visits'] });
    },
  });
}

// Service Area Mutations
export function useCreateServiceAreaMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateServiceAreaRequest) => createServiceArea(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-areas'] });
    },
  });
}

export function useUpdateServiceAreaMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateServiceAreaRequest }) =>
      updateServiceArea(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-areas'] });
    },
  });
}

export function useDeleteServiceAreaMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteServiceArea(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-areas'] });
    },
  });
}

// Notification Mutations
//
// These optimistically rewrite the cached infinite-query pages via setQueriesData
// instead of invalidating. Invalidation triggers a full refetch that rebuilds the
// page array, changing the list height and resetting the dropdown's scroll position
// mid-read. Mutating the cached pages in place keeps the loaded pages — and the
// scrollTop — stable.

type NotificationPages = InfiniteData<GetNotificationsResponse, string | undefined>;

/**
 * Apply a per-notification transform across every cached page of the infinite
 * notifications query, adjusting each page's unreadCount by the read-state change.
 *
 * unreadCount is the server-wide total, not the count of unread items on this
 * page. Per-item deltas are correct for a single mark-read/unread. Mark-all
 * must not use this helper — subtracting only the loaded unread items leaves a
 * leftover badge (RUT-3472).
 */
export function updateNotificationPages(
  data: NotificationPages | undefined,
  transform: (notification: GetNotificationsResponse['notifications'][number]) => {
    notification: GetNotificationsResponse['notifications'][number];
    unreadDelta: number;
  },
): NotificationPages | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => {
      let unreadDelta = 0;
      const notifications = page.notifications.map((notification) => {
        const result = transform(notification);
        unreadDelta += result.unreadDelta;
        return result.notification;
      });
      return {
        ...page,
        notifications,
        unreadCount: Math.max(0, page.unreadCount + unreadDelta),
      };
    }),
  };
}

/**
 * Mark every loaded notification as read and zero the server-wide unreadCount
 * on each page. The API marks all notifications, including ones not yet
 * loaded, so leftover unreadCount must go to 0 rather than being decremented
 * by the items currently in cache (RUT-3472).
 */
export function markAllNotificationPagesRead(
  data: NotificationPages | undefined,
): NotificationPages | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      notifications: page.notifications.map((n) => (n.read ? n : { ...n, read: true })),
      unreadCount: 0,
    })),
  };
}

export function useMarkNotificationAsReadMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notificationId: string) => {
      return markNotificationAsRead(notificationId);
    },
    onSuccess: (_data, notificationId) => {
      queryClient.setQueriesData<NotificationPages>({ queryKey: ['notifications'] }, (data) =>
        updateNotificationPages(data, (notification) => {
          if (notification.id !== notificationId || notification.read) {
            return { notification, unreadDelta: 0 };
          }
          return { notification: { ...notification, read: true }, unreadDelta: -1 };
        }),
      );
    },
  });
}

export function useMarkNotificationAsUnreadMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notificationId: string) => {
      return markNotificationAsUnread(notificationId);
    },
    onSuccess: (_data, notificationId) => {
      queryClient.setQueriesData<NotificationPages>({ queryKey: ['notifications'] }, (data) =>
        updateNotificationPages(data, (notification) => {
          if (notification.id !== notificationId || !notification.read) {
            return { notification, unreadDelta: 0 };
          }
          return { notification: { ...notification, read: false }, unreadDelta: 1 };
        }),
      );
    },
  });
}

export function useMarkAllNotificationsAsReadMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return markAllNotificationsAsRead();
    },
    onSuccess: () => {
      queryClient.setQueriesData<NotificationPages>(
        { queryKey: ['notifications'] },
        markAllNotificationPagesRead,
      );
    },
  });
}

// Crew Mutations
export function useUpdateCrewMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ crewId, data }: { crewId: string; data: any }) => {
      return updateCrew(crewId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.crews.all() });
    },
  });
}

export function useUpdateCrewMemberRoleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ crewId, memberId, role }: { crewId: string; memberId: string; role: 'leader' | 'member' }) => {
      return updateCrewMemberRole(crewId, memberId, { role });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.crews.all() });
    },
  });
}

export function useRemoveMemberFromCrewMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ crewId, memberId }: { crewId: string; memberId: string }) => {
      return removeMemberFromCrew(crewId, memberId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.crews.all() });
    },
  });
}

// Work Queue Mutations
export function useUpdateSubscriptionScheduleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customerId,
      serviceTemplateId,
      data
    }: {
      customerId: string;
      serviceTemplateId: string;
      data: {
        preferredDayOfWeek?: number;
        preferredDayOfMonth?: number;
        preferredMonth?: number;
        preferredDayOfYear?: number;
        assignedCrewId?: string;
        order?: number;
      };
    }) => {
      return updateSubscriptionSchedule(customerId, serviceTemplateId, data);
    },
    onSuccess: () => {
      // Invalidate work queue queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['work-queue', 'subscriptions'] });
    },
  });
}

// User Profile Mutations
export function useUpdateUserProfileMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateUserProfileRequest) => updateUserProfile(data),
    onSuccess: (_result, variables) => {
      // Update cache directly without refetching to avoid re-render cycles
      queryClient.setQueryData(queryKeys.users.profile(), (oldData: unknown) => {
        if (!oldData || typeof oldData !== 'object') return oldData;
        const typed = oldData as { profile: Record<string, unknown>; notificationPreferences: unknown };
        return {
          ...typed,
          profile: {
            ...typed.profile,
            ...variables,
          },
        };
      });
    },
  });
}

export function useUpdateNotificationPreferencesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (preferences: NotificationPreferences) => updateNotificationPreferences(preferences),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.profile() });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.notificationPreferences() });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// Notification Template Mutations
export function useUpdateNotificationTemplateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ notificationType, data }: { notificationType: string; data: { emailSubject?: string; emailContent?: string; smsContent?: string; enabled?: boolean } }) =>
      updateNotificationTemplate(notificationType, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notificationTemplates.all() });
    },
  });
}

export function useResetNotificationTemplateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationType: string) => resetNotificationTemplate(notificationType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notificationTemplates.all() });
    },
  });
}

export function useSendWelcomeEmailsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return sendWelcomeEmails();
    },
    onSuccess: async () => {
      // Wait for DynamoDB stream to update Typesense before refetching
      await new Promise((resolve) => setTimeout(resolve, 2000));
      // Invalidate the welcome email pending count to refresh the dashboard panel
      queryClient.invalidateQueries({ queryKey: ['welcome-email-pending-count'] });
      // Also invalidate typesense customers in case the list is being viewed
      queryClient.invalidateQueries({ queryKey: ['typesense-customers'] });
    },
  });
}